import {
  List,
  TextField,
  FunctionField,
  EditButton,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  SearchInput,
  CreateButton,
  useDataProvider,
  useNotify,
  useRefresh,
  useRedirect,
  useRecordContext,
  Button,
} from 'react-admin';
import { useState } from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { useGetList as useRaGetList } from 'react-admin';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import { BANKS, bankNameByBin } from '../services/vietqr';
import { ImportButton } from '../components/ImportButton';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveDataList } from '../components/ResponsiveDataList';
import { StatRow } from '../components/StatCards';
import { exportRows } from '../services/excelExport';
import { pick, str } from '../utils/rows';
import { brand } from '../theme';
import { formPageSx, pageShellSx, resourceListSx } from '../ui';
import type { Supplier } from '../types';
import { syncSuppliersFromMeInvoice } from '../services/meInvoiceSync';

const bankChoices = BANKS.map((b) => ({ id: b.bin, name: `${b.name} (${b.bin})` }));

export const mapSupplierRow = (row: Record<string, any>) => {
  const name = pick(row, ['tên', 'ncc', 'nhà cung cấp', 'name', 'công ty']);
  if (!name) return null;
  const supplierName = str(name);
  return {
    name: supplierName,
    taxCode: str(pick(row, ['mst', 'mã số thuế', 'tax'])),
    bankName: str(pick(row, ['ngân hàng', 'bank'])),
    bankBin: str(pick(row, ['bin'])),
    accountNumber: str(pick(row, ['số tk', 'stk', 'tài khoản', 'account'])),
    accountHolder: supplierName,
    phone: str(pick(row, ['đt', 'điện thoại', 'phone', 'sđt'])),
    address: str(pick(row, ['địa chỉ', 'address'])),
  };
};

const initials = (name?: string) =>
  (name || '?')
    .replace(/\b(CÔNG TY|CTY|TNHH|MTV|CP|TM|NPP)\b/gi, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const NameCell = () => (
  <FunctionField
    render={(r: Supplier) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 230 }}>
        <Avatar sx={{ width: 38, height: 38, fontSize: 13, fontWeight: 800, background: brand.avatarGradient, boxShadow: '0 8px 18px -11px rgba(14,82,99,.75)' }}>
          {initials(r.name)}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 750 }}>{r.name}</Typography>
      </Box>
    )}
  />
);

const ExportButton = () => {
  const dp = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const { data } = await dp.getList<Supplier>('suppliers', {
        pagination: { page: 1, perPage: 10000 }, sort: { field: 'name', order: 'ASC' }, filter: {},
      });
      exportRows(
        data.map((s) => ({
          'Tên NCC': s.name, MST: s.taxCode || '', 'Ngân hàng': s.bankName || '',
          'Số TK': s.accountNumber || '', 'Chủ TK': s.accountHolder || '', ĐT: s.phone || '', 'Địa chỉ': s.address || '',
        })),
        'nha-cung-cap', 'NCC',
      );
      notify(`Đã xuất ${data.length} NCC ra Excel`, { type: 'success' });
    } catch {
      notify('Không thể xuất tệp Excel. Vui lòng thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  return <Button label="Xuất" onClick={onClick} disabled={loading} startIcon={<DownloadRoundedIcon />} />;
};

const MeInvoiceSupplierSyncButton = () => {
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const result = await syncSuppliersFromMeInvoice(dp);
      notify(`meInvoice: ${result.created} thêm, ${result.updated} cập nhật, ${result.skipped} bỏ qua, ${result.invalid} không hợp lệ.`, { type: result.invalid ? 'warning' : 'success' });
      refresh();
    } catch (error: any) {
      notify(String(error?.message || 'Không thể đồng bộ nhà cung cấp từ meInvoice.'), { type: 'error' });
    } finally { setLoading(false); }
  };
  return <Button label="Lấy NCC meInvoice" onClick={onClick} disabled={loading} startIcon={<CloudSyncRoundedIcon />} />;
};

const SupplierImportButton = () => {
  return <ImportButton resource="suppliers" mapRow={mapSupplierRow} label="Nhập NCC (Excel)" />;
};

const SupplierStats = () => {
  const { data = [] } = useRaGetList<Supplier>('suppliers', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' },
  });
  const total = data.length || 0;
  const withBank = data.filter((s) => s.accountNumber && s.bankBin).length;
  const banks = new Set(data.map((s) => s.bankName).filter(Boolean));
  const withPhone = data.filter((s) => s.phone).length;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <StatRow
      stats={[
        { label: 'Tổng nhà cung cấp', value: total, sub: 'Danh sách đã lưu', icon: <GroupsRoundedIcon />, color: '#0E7490' },
        { label: 'Đầy đủ thông tin NH', value: `${pct(withBank)}%`, sub: `${withBank}/${total} NCC đã có thông tin`, icon: <VerifiedRoundedIcon />, color: '#2F80ED' },
        { label: 'Ngân hàng sử dụng', value: banks.size, sub: [...banks].slice(0, 2).join(', ') || 'Chưa có dữ liệu', icon: <AccountBalanceRoundedIcon />, color: '#0FAF78' },
        { label: 'Có số điện thoại', value: `${pct(withPhone)}%`, sub: `${withPhone}/${total} NCC có số ĐT`, icon: <PhoneRoundedIcon />, color: '#F59E0B' },
      ]}
    />
  );
};

const supplierFilters = [
  <SearchInput key="q" source="q" placeholder="Tìm kiếm nhà cung cấp..." alwaysOn />,
  <SelectInput key="bankBin" source="bankBin" label="Lọc theo ngân hàng" choices={bankChoices} alwaysOn />,
];

export const SupplierList = () => (
  <Box sx={pageShellSx}>
    <PageHeader
      title="Nhà cung cấp"
      subtitle="Quản lý danh sách nhà cung cấp và thông tin tài khoản ngân hàng."
      icon={<StoreRoundedIcon />}
      actions={
        <>
          <SupplierImportButton />
          <MeInvoiceSupplierSyncButton />
          <CreateButton label="Tạo mới" variant="contained" />
          <ExportButton />
        </>
      }
    />
    <SupplierStats />
    <List sx={resourceListSx} actions={false} filters={supplierFilters} perPage={25} title="Nhà cung cấp" empty={false}>
      <ResponsiveDataList<Supplier>
        ariaLabel="Danh sách nhà cung cấp"
        primaryText={(r) => r.name || '(chưa có tên)'}
        secondaryText={(r) => `${bankNameByBin(r.bankBin) || r.bankName || 'Chưa rõ ngân hàng'}${r.accountNumber ? ` · ${r.accountNumber}` : ''}`}
        tertiaryText={(r) => r.taxCode ? `MST ${r.taxCode}` : 'Chưa có mã số thuế'}
        leftAvatar={(r) => initials(r.name)}
      >
        <FunctionField label="Tên NCC" render={() => <NameCell />} />
        <TextField source="taxCode" label="MST" emptyText="—" />
        <FunctionField label="Ngân hàng" render={(r: Supplier) => bankNameByBin(r.bankBin) || r.bankName || '—'} />
        <TextField source="accountNumber" label="Số TK" emptyText="—" />
        <TextField source="accountHolder" label="Chủ TK" emptyText="—" />
        <TextField source="phone" label="ĐT" emptyText="—" />
        <EditButton label="Sửa" />
      </ResponsiveDataList>
    </List>
  </Box>
);

const transform = (d: any) => {
  const supplierName = String(d.name || '').trim();
  const bankName = bankNameByBin(d.bankBin) || d.bankName;
  const existing = Array.isArray(d.bankAccounts)
    ? d.bankAccounts.map((account: any) => ({ ...account, accountHolder: supplierName }))
    : [];
  const accountNumber = String(d.accountNumber || '').trim();
  let bankAccounts = existing;
  if (accountNumber) {
    const current = existing.find((account: any) => String(account.accountNumber) === accountNumber) || {};
    bankAccounts = [
      ...existing.filter((account: any) => String(account.accountNumber) !== accountNumber).map((account: any) => ({ ...account, isPrimary: false })),
      {
        ...current,
        accountNumber,
        bankName,
        bankBin: d.bankBin,
        accountHolder: supplierName,
        isPrimary: true,
        mappingSource: 'manual',
        mappingConfidence: 1,
      },
    ];
  }
  return {
    ...d,
    name: supplierName,
    bankName,
    accountHolder: supplierName,
    bankAccounts,
    lastSyncSource: 'user',
  };
};

const AccountNumberInput = () => {
  const record = useRecordContext<Supplier>();
  const accounts = record?.bankAccounts || [];
  if (accounts.length <= 1) return <TextInput source="accountNumber" label="Số tài khoản" fullWidth />;
  return (
    <SelectInput
      source="accountNumber"
      label="Tài khoản chính"
      choices={accounts.map((account) => ({
        id: account.accountNumber,
        name: `${account.accountNumber} — ${account.bankName || 'Chưa rõ ngân hàng'}`,
      }))}
      helperText="Chọn một tài khoản chính dùng cho lệnh chi."
      fullWidth
    />
  );
};

const FormFields = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, width: '100%' }}>
    <TextInput source="name" label="Tên NCC" fullWidth sx={{ gridColumn: { md: '1 / -1' } }} />
    <TextInput source="taxCode" label="MST" fullWidth />
    <SelectInput source="bankBin" label="Ngân hàng" choices={bankChoices} fullWidth />
    <AccountNumberInput />
    <TextInput source="accountHolder" label="Chủ tài khoản" helperText="Tự động lấy theo tên nhà cung cấp khi lưu." fullWidth disabled />
    <TextInput source="phone" label="Điện thoại" fullWidth />
    <TextInput source="address" label="Địa chỉ" fullWidth />
    <TextInput source="note" label="Ghi chú" fullWidth multiline minRows={3} sx={{ gridColumn: { md: '1 / -1' } }} />
  </Box>
);

export const SupplierEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const onSuccess = () => {
    notify('Đã cập nhật nhà cung cấp.', { type: 'success' });
    refresh();
  };
  return (
    <Box sx={pageShellSx}>
      <PageHeader title="Cập nhật nhà cung cấp" subtitle="Chỉnh sửa thông tin pháp lý và tài khoản thanh toán của nhà cung cấp." icon={<StoreRoundedIcon />} />
      <Edit title="Sửa NCC" transform={transform} mutationOptions={{ onSuccess }} sx={formPageSx}>
        <SimpleForm><FormFields /></SimpleForm>
      </Edit>
    </Box>
  );
};

export const SupplierCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const onSuccess = (data: Supplier) => {
    redirect('edit', 'suppliers', data.id);
    notify('Đã tạo nhà cung cấp.', { type: 'success' });
  };
  return (
    <Box sx={pageShellSx}>
      <PageHeader title="Thêm nhà cung cấp" subtitle="Tạo hồ sơ nhà cung cấp mới cho quy trình đối soát và thanh toán." icon={<StoreRoundedIcon />} />
      <Create title="Thêm NCC" transform={transform} mutationOptions={{ onSuccess }} sx={formPageSx}>
        <SimpleForm><FormFields /></SimpleForm>
      </Create>
    </Box>
  );
};
